import { FC, useState } from "react";
import { Box } from "@mui/material";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { CustomButton } from "../../../components";
import { pxToRem } from "../../../util";
import { Colors } from "../../../configs/colors";

interface IAddNoteModalProps {
  open: boolean;
  onClose: () => void;
  onAdd?: (note: string) => void;
  initialNote?: string;
}

const modules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const formats = ["bold", "italic", "underline", "strike", "list", "link"];

const AddNoteModal: FC<IAddNoteModalProps> = ({ open, onClose, onAdd, initialNote }) => {
  const [note, setNote] = useState(initialNote ?? "");

  const isNoteEmpty = (html: string) =>
    !html || !html.replace(/<(.|\n)*?>/g, "").trim();

  return (
    <CustomModal
      title="Add Notes"
      openModal={open}
      onClose={onClose}
      width={pxToRem(657)}
      height={pxToRem(560)}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: pxToRem(24),
          "& .quill": {
            width: pxToRem(589),
            border: `${pxToRem(1)} solid ${Colors.MAIN_GRAY}`,
            borderRadius: pxToRem(5),
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
          "& .ql-toolbar": {
            borderBottom: `${pxToRem(1)} solid ${Colors.MAIN_GRAY}`,
            borderTop: "none",
            borderLeft: "none",
            borderRight: "none",
            background: Colors.WHITE,
            borderTopLeftRadius: pxToRem(5),
            borderTopRightRadius: pxToRem(5),
            fontFamily: "inherit",
          },
          "& .ql-toolbar .ql-stroke": { stroke: Colors.MAIN_GRAY },
          "& .ql-toolbar .ql-fill": { fill: Colors.MAIN_GRAY },
          "& .ql-toolbar button:hover .ql-stroke": { stroke: Colors.PRIMARY },
          "& .ql-toolbar button:hover .ql-fill": { fill: Colors.PRIMARY },
          "& .ql-toolbar button.ql-active .ql-stroke": { stroke: Colors.PRIMARY },
          "& .ql-toolbar button.ql-active .ql-fill": { fill: Colors.PRIMARY },
          "& .ql-container": {
            border: "none",
            fontSize: pxToRem(16),
            fontFamily: "inherit",
            color: Colors.MAIN_GRAY,
          },
          "& .ql-editor": {
            height: pxToRem(245),
            maxHeight: pxToRem(245),
            overflowY: "auto",
            padding: `${pxToRem(12)} ${pxToRem(16)}`,
          },
          "& .ql-editor.ql-blank::before": {
            color: `${Colors.MAIN_GRAY}80`,
            fontStyle: "normal",
            fontSize: pxToRem(14),
          },
        }}
      >
        <ReactQuill
          theme="snow"
          value={note}
          onChange={setNote}
          modules={modules}
          formats={formats}
          placeholder="Type your notes here..."
        />

        <CustomButton
          sx={{
            width: pxToRem(467),
            height: pxToRem(64),
            fontSize: pxToRem(24),
            fontWeight: "bold",
            borderRadius: pxToRem(10),
            background: Colors.PRIMARY,
            marginTop: pxToRem(6),
          }}
          disabled={isNoteEmpty(note)}
          onClick={() => {
            onAdd?.(note);
            onClose();
            setNote("");
          }}
        >
          Add
        </CustomButton>
      </Box>
    </CustomModal>
  );
};

export default AddNoteModal;
